import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from '../../../i18n/useTranslation';
import { WEEKDAY_KEYS } from '../../../i18n/weekdays';
import type { Weekday } from '../../../Models/enums';
import type { PublicIntakeFormModel } from '../../../Models/PublicModel';
import { PublicService } from '../../../Services/PublicService';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import './IntakeForm.css';

interface Range {
  weekday: Weekday;
  startTime: string;
  endTime: string;
}

const WEEKDAYS: Weekday[] = [0, 1, 2, 3, 4, 5, 6];

/**
 * The form a tutor's client fills in themselves.
 *
 * Public, because the person filling it has no account here. The page asks for
 * the least it can get away with: a name is the only required field, since a
 * form that refuses to submit is a form the tutor never receives.
 *
 * Availability is collected as real ranges rather than "afternoon", because a
 * vague answer was ambiguous enough to schedule a lesson wrongly.
 */
export function IntakeForm() {
  const { token } = useParams();
  const { tr } = useTranslation();
  const [form, setForm] = useState<PublicIntakeFormModel | null>(null);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [entryCode, setEntryCode] = useState('');
  const [notes, setNotes] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [ranges, setRanges] = useState<Range[]>([]);

  useEffect(() => {
    if (!token) {
      setFailed(true);
      return;
    }
    PublicService.intakeForm(token)
      .then(setForm)
      .catch(() => setFailed(true));
  }, [token]);

  function toggleDay(weekday: Weekday) {
    setRanges(current => current.some(range => range.weekday === weekday)
      ? current.filter(range => range.weekday !== weekday)
      // Half-hour steps inside a sane teaching day, matching what the server
      // will accept. Anything else it drops, so offering it would be a lie.
      : [...current, { weekday, startTime: '16:00', endTime: '19:00' }]);
  }

  function setRange(weekday: Weekday, key: 'startTime' | 'endTime', value: string) {
    setRanges(current => current.map(range =>
      range.weekday === weekday ? { ...range, [key]: value } : range));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!token) return;
    setBusy(true);
    try {
      await PublicService.submitIntakeForm(token, {
        firstName,
        lastName,
        phone,
        address,
        entryCode,
        notes,
        availability: ranges,
        contacts: contactName ? [{ name: contactName, phone: contactPhone, receivesPayments: true }] : []
      });
      setDone(true);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  if (failed && !done) {
    return <EmptyState title={tr('intakeFormTitle')} hint={tr('intakeFormInvalid')} />;
  }
  if (done) {
    return <EmptyState title={tr('intakeFormThanks')} />;
  }
  if (!form) return <Spinner />;

  return (
    <section className="intake-form" dir={form.language === 'en' ? 'ltr' : 'rtl'}>
      <header className="intake-form-header">
        {form.logo && <img className="intake-form-logo" src={form.logo} alt="" />}
        <h1>{form.tutorName}</h1>
      </header>
      {form.note && <p className="intake-form-note">{form.note}</p>}

      <form onSubmit={submit}>
        <div className="intake-form-grid">
          <label className="field">
            <span>{tr('firstName')}</span>
            <input className="text-input" value={firstName} onChange={e => setFirstName(e.target.value)} required />
          </label>
          <label className="field">
            <span>{tr('lastName')}</span>
            <input className="text-input" value={lastName} onChange={e => setLastName(e.target.value)} />
          </label>
          <label className="field">
            <span>{tr('phone')}</span>
            <input className="text-input" type="tel" value={phone} onChange={e => setPhone(e.target.value)} />
          </label>
          <label className="field">
            <span>{tr('entryCode')}</span>
            <input className="text-input" value={entryCode} onChange={e => setEntryCode(e.target.value)} />
          </label>
          <label className="field is-wide">
            <span>{tr('address')}</span>
            <input className="text-input" value={address} onChange={e => setAddress(e.target.value)} />
          </label>
        </div>

        <fieldset className="intake-form-days">
          <legend>{tr('availabilityTitle')}</legend>
          {WEEKDAYS.map(weekday => {
            const range = ranges.find(entry => entry.weekday === weekday);
            return (
              <div key={weekday} className="intake-form-day">
                <label className="check-label">
                  <input
                    type="checkbox"
                    checked={Boolean(range)}
                    onChange={() => toggleDay(weekday)}
                  />
                  <span>{tr(WEEKDAY_KEYS[weekday])}</span>
                </label>
                {range && (
                  <div className="intake-form-times">
                    <input
                      className="text-input"
                      type="time"
                      step={1800}
                      value={range.startTime}
                      onChange={e => setRange(weekday, 'startTime', e.target.value)}
                      aria-label={`${tr(WEEKDAY_KEYS[weekday])} ${tr('from')}`}
                    />
                    <input
                      className="text-input"
                      type="time"
                      step={1800}
                      value={range.endTime}
                      onChange={e => setRange(weekday, 'endTime', e.target.value)}
                      aria-label={`${tr(WEEKDAY_KEYS[weekday])} ${tr('until')}`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </fieldset>

        <div className="intake-form-grid">
          <label className="field">
            <span>{tr('contactName')}</span>
            <input className="text-input" value={contactName} onChange={e => setContactName(e.target.value)} />
          </label>
          <label className="field">
            <span>{tr('contactPhone')}</span>
            <input className="text-input" type="tel" value={contactPhone} onChange={e => setContactPhone(e.target.value)} />
          </label>
          <label className="field is-wide">
            <span>{tr('notes')}</span>
            <textarea className="text-input" rows={3} value={notes} onChange={e => setNotes(e.target.value)} />
          </label>
        </div>

        <button type="submit" className="primary-button intake-form-submit" disabled={busy || !firstName.trim()}>
          {busy ? <Spinner inline /> : tr('send')}
        </button>
      </form>
    </section>
  );
}
